import React, { useState, useEffect } from 'react'
import { Stack } from '@mantine/core'
import MainTable from 'app/views/components/Table/MainTable'
import { useListState } from '@mantine/hooks'
import { getTodos, getTodoLoader, isTodosStale } from 'app/state/ducks/todos/selectors'
import { loadTodos } from 'app/state/ducks/todos/thunks'
import { ToDoParams } from 'app/models/todo-params'
import { useDispatch, useSelector } from 'react-redux'
import { colsTodo } from './todo/todo-columnDefs'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { myworkKPIData } from './mywork-kpi-data'
import { todoKpis } from './mywork-util/todo-kpis'
import {
  GridSortingStorage,
  GridPaginationStorage,
  GridColumnVisibilityStorage,
  useGridSorting,
  useGridPagination,
  useGridColumnVisibility,
} from 'app/utils/grid-sorting-storage'

interface props {
  date: Date
}

export function ToDoPage({ date }: props) {
  const dispatch = useDispatch()

  const todos: ToDoParams[] = useSelector(getTodos) || []
  const todoLoader = useSelector(getTodoLoader)
  const todosStale = useSelector(isTodosStale)
  const [globalLoading, setGlobalLoading] = useState(false)
  const [todoTable, setTodoTable] = useListState<ToDoParams>([])

  // Grid preferences hooks
  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.myworkTodo,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.myworkTodo,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.myworkTodo)

  const formattedTodos = (todos: ToDoParams[]) => {
    const sortedTodos = todos.sort((a, b) => {
      const timeDueA = new Date(a.timeDue).getTime()
      const timeDueB = new Date(b.timeDue).getTime()
      return timeDueA - timeDueB
    })
    const statusUpdated = sortedTodos.map((todo: ToDoParams) => {
      if (new Date(todo.timeDue) < new Date(date) && todo.status !== 'Completed') {
        todo.overdue = true
      } else {
        todo.overdue = false
      }
      return todo
    })
    return statusUpdated
  }

  useEffect(() => {
    setGlobalLoading(todoLoader)
  }, [todoLoader])

  // the tab's one read; and once more when a write dropped a read in flight
  useEffect(() => {
    dispatch(loadTodos())
  }, [])
  useEffect(() => {
    if (todosStale && !todoLoader) dispatch(loadTodos())
  }, [todosStale, todoLoader])

  useEffect(() => {
    const updatedTodos = formattedTodos(todos)
    setTodoTable.setState(updatedTodos)
  }, [todos, date])

  const { percentageComplete, deadlineApproaching: deadLine } = todoKpis(todoTable)

  const kpiData = myworkKPIData({
    activityCount: todoTable.length,
    percentageComplete: percentageComplete,
    deadlineApproaching: deadLine,
    totalActivityAmount: 'N/A',
  })

  return (
    <>
      <Stack w="100%">
        <KPI_Section kpiData={kpiData} isLoading={globalLoading} />
        <MainTable
          tableName="Todo Table"
          isLoading={globalLoading}
          withPagination={true}
          csvExportEnabled={true}
          enableTopToolbar={false}
          headerBackgroundColor="#F0EEEE"
          overflow="hidden"
          minHeight="0px"
          columnDefs={colsTodo}
          data={todoTable}
          initialSorting={[
            { id: sorting.sortBy, desc: sorting.order === 'desc' },
          ]}
          onSortingChange={handleSortingChange}
          initialPagination={pagination}
          onPaginationChange={handlePaginationChange}
          initialColumnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          setRow={(e: ToDoParams, cellName: string) => {
            if (cellName != 'id') {
              console.log('row clicked', e)
            }
          }}
        />
      </Stack>
    </>
  )
}
